import { ChangeEvent, FC, useState } from 'react'

import styled from 'styled-components'
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { PrimaryInfoModal } from 'src/Components/PrimaryInfoModal'
import { PrimaryPhoneInput } from 'src/Components/PrimaryPhoneInput'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { getCountry } from 'src/Redux/auth-process/countrySlice/selectors'
import { refetchUsersMe } from 'src/Redux/userInfoApi'
import { useUpdateArtistPhoneNumberMutation } from 'src/Redux/artistProfileApi'
import { variables } from 'src/Styled/variables'
import SuccessIcon from 'src/Assets/Svg/pages-icons/Success.svg?react'

const StyledModal = styled(PrimaryInfoModal)`
  && {
    max-width: 400px;
  }
`

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 24px;
`

const Text = styled.p`
  margin: 0;
  width: 100%;
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
`

const InputWrapper = styled.div`
  width: 100%;
`

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  color: ${({ theme }) => theme.color.brandGreen};

  svg {
    flex-shrink: 0;
  }
`

export interface GetSmsWhenApprovedModalProps {
  isOpen: boolean
  onClose: () => void
}

export const GetSmsWhenApprovedModal: FC<GetSmsWhenApprovedModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch()
  const country = useAppSelector(getCountry)

  const [updateArtistPhoneNumber, { isLoading }] =
    useUpdateArtistPhoneNumberMutation()

  const [phoneNumber, setPhoneNumber] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isSaved, setIsSaved] = useState(false)

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPhoneNumber(event.target.value)
  }

  const onSubmit = (): void => {
    updateArtistPhoneNumber({ phoneNumber })
      .unwrap()
      .then(() => {
        setIsSaved(true)
        void dispatch(refetchUsersMe())
      })
      .catch(() => {
        setIsSaved(false)
      })
  }

  const isSubmitDisabled =
    isLoading ||
    Boolean(validationError) ||
    !isValidPhoneNumber(phoneNumber, country as CountryCode)

  const handleClose = (): void => {
    setIsSaved(false)
    setPhoneNumber('')
    setValidationError(null)
    onClose()
  }

  return (
    <StyledModal
      isOpen={isOpen || isSaved}
      onClose={handleClose}
      title='Get an SMS when approved'
    >
      <Container>
        {isSaved ? (
          <SuccessMessage>
            <SuccessIcon width={16} height={16} />
            <span>SMS notifications enabled</span>
          </SuccessMessage>
        ) : (
          <>
            <Text>
              We&rsquo;ll text you the moment your campaign is approved and
              ready to launch.
            </Text>
            <InputWrapper>
              <PrimaryPhoneInput
                value={phoneNumber}
                placeholder='Enter phone number'
                country={country as CountryCode}
                autoComplete='tel'
                onChange={onChange}
                onValidationError={setValidationError}
              />
            </InputWrapper>
            <CTAButton
              type='button'
              disabled={isSubmitDisabled}
              onClick={onSubmit}
            >
              Notify me by SMS
            </CTAButton>
          </>
        )}
      </Container>
    </StyledModal>
  )
}
